import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayStr } from "@/components/Pager";
import { fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ApprovalBadge, RejectReason } from "@/components/ApprovalBadge";
import { Utensils, Moon, Bath, Smile, HeartPulse, MessageSquare, Loader2 } from "lucide-react";

export const ACTIVITY_FIELDS = [
  ["food_intake", "Food", Utensils, "e.g. Ate full lunch, little snack"],
  ["nap_time", "Nap", Moon, "e.g. 1:00 – 2:15 pm"],
  ["washroom", "Washroom", Bath, "e.g. 2 times, diaper changed"],
  ["behavior", "Mood / behaviour", Smile, "e.g. Happy, played well"],
  ["health_observation", "Health", HeartPulse, "e.g. Mild cold"],
  ["teacher_remarks", "Note for parents", MessageSquare, "Anything else"],
];
export const QUICK = {
  food_intake: ["Ate all", "Ate half", "Ate little", "Didn't eat"],
  nap_time: ["Slept well", "Short nap", "Restless", "No nap"],
  washroom: ["Used toilet", "1 time", "2 times", "Diaper changed", "Accident"],
  behavior: ["Happy", "Playful", "Calm", "Cranky", "Upset"],
  health_observation: ["Healthy", "Mild cold", "Cough", "Fever", "Tired"],
  teacher_remarks: ["Great day!", "Please send spare clothes", "Needs more rest"],
};
function QuickChips({ k, value, onPick }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {(QUICK[k] || []).map((c) => {
        const on = value === c;
        return <button type="button" key={c} data-testid={`da-quick-${k}-${c.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => onPick(on ? "" : c)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{c}</button>;
      })}
    </div>
  );
}
const LOG_ROLES = ["teacher"];

function Entry({ u, staff }) {
  return (
    <div data-testid={`da-entry-${u.id}`} className="rounded-xl border border-slate-100 p-3 transition-shadow hover:shadow-sm">
      <div className="text-xs text-slate-500 mb-2 flex flex-wrap items-center gap-2"><span className="font-semibold text-slate-800">{fmtDate(u.date)}</span> · by {u.teacher_name}{staff && <ApprovalBadge status={u.approval} testid={`da-status-${u.id}`} />}</div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {ACTIVITY_FIELDS.filter(([k]) => u[k]).map(([k, l, Icon]) => (
          <div key={k} className="flex gap-2 text-sm"><Icon size={14} className="text-violet-600 mt-0.5 shrink-0" /><span><span className="font-semibold text-slate-700">{l}:</span> <span className="text-slate-600">{u[k]}</span></span></div>
        ))}
      </div>
      {staff && <RejectReason item={u} />}
    </div>
  );
}

export function DailyActivityPanel({ student }) {
  const { user } = useAuth();
  const canLog = LOG_ROLES.includes(user?.role) && !user?.view_only;
  const [items, setItems] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const today = todayStr();
  const load = () => api.get("/student-updates", { params: { student_id: student.id, limit: 10 } }).then((r) => {
    setItems(r.data.items);
    const t = r.data.items.find((x) => x.date === today);
    setF(t ? Object.fromEntries(ACTIVITY_FIELDS.map(([k]) => [k, t[k] || ""])) : {});
  }).catch(() => setItems([]));
  useEffect(() => { load(); }, [student.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const todays = items?.find((x) => x.date === today);
  const mine = !todays || user?.role !== "teacher" || todays.teacher_id === user?.id;
  async function save() {
    setBusy(true);
    try {
      const body = { student_id: student.id, date: today, photos: todays?.photos || [], classroom_activities: todays?.classroom_activities || "", learning_activities: todays?.learning_activities || "",
        ...Object.fromEntries(ACTIVITY_FIELDS.map(([k]) => [k, f[k] || ""])) };
      if (todays) await api.put(`/student-updates/${todays.id}`, body); else await api.post("/student-updates", body);
      toast.success("Saved — sent for approval before parents see it"); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div data-testid="daily-activity-panel" className="space-y-3">
      {canLog && mine && (
        <div className="rounded-2xl bg-violet-50/60 border border-violet-100 p-4 space-y-3">
          <div className="font-display font-bold text-slate-900">Today · {student.name}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ACTIVITY_FIELDS.map(([k, l, Icon, ph]) => (
              <div key={k}><Label className="text-xs flex items-center gap-1"><Icon size={12} />{l}</Label>
                <Input data-testid={`da-${k}`} value={f[k] || ""} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 bg-white" />
                <QuickChips k={k} value={f[k] || ""} onPick={(v) => setF({ ...f, [k]: v })} /></div>
            ))}
          </div>
          <Button data-testid="da-save" className="btn-brand" disabled={busy || !ACTIVITY_FIELDS.some(([k]) => (f[k] || "").trim())} onClick={save}>
            {busy && <Loader2 size={14} className="animate-spin mr-1" />}{todays ? "Update today's activity" : "Save today's activity"}</Button>
        </div>
      )}
      {items === null ? <Loader2 className="animate-spin text-violet-500" /> : items.length === 0
        ? <p data-testid="da-empty" className="text-sm text-slate-500">No daily activity logged yet.</p>
        : items.map((u) => <Entry key={u.id} u={u} staff={user?.role !== "parent"} />)}
    </div>
  );
}
